---
title: Sync and conflicts
lede: Bring the branch the session was cut from up to date, and resolve what conflicts.
order: 28
part: Review and merge
---

A session is cut from a branch at one moment. When that branch gets new commits, on your computer or on its remote, sync brings them into the session so the diff stays against the present.

![The review footer, once the branch has moved](media/sync-footer.webp)

### Syncing

Sync with **Sync** on the review footer (it reads **Branch moved** when there is something to bring in), **Session › Sync from `<branch>`**, or `⌘⌥S`. While a review is open, Airlock looks at the branch on its remote in the background, at most once every five minutes.

For each repository, sync fetches the branch from its remote, then **rebases** the session's work onto the new tip, your computer's unpushed commits included. If your copy and the remote's have diverged, it rebases onto your copy and says to pull the branch first. If the fetch fails, the sync stops there and says why. Uncommitted work is set aside and put back. The diff moves with it, so it still shows only the session's own changes.

### Automatic sync

When the agent hands back and the source branch has moved, Airlock syncs the session for you. If that sync would hit conflicts, it is abandoned at once, a notice says so, and **Sync** stays on the footer for you to run when ready.

Turn it off with **Settings › Git & merge › Sync when the agent hands back** (on by default). A workspace can override it.

### When a sync stops on conflicts

A sync you run yourself stops on conflicts instead of giving up. The footer reads **Sync stopped**, and each conflicted file is marked `!` in the Changes list.

Resolve each file, then press **Sync again** to finish the rebase. **Abandon** puts the session back where it was before the sync, uncommitted work included; what you resolved is dropped.

A merge waits while a sync is stopped.

### Resolving a file yourself

A conflicted file's block shows a three-pane merge view: the branch you synced from on the left, the session's work on the right, and the result, editable, in the middle. Each side is named by its branch.

![The three-pane merge view](media/merge-view.webp)

1. For each conflict, take a side with the arrow beside it, press **Take both**, or edit the result.
2. Press **Ask Agent** on a single conflict to have the agent write that region; its answer lands in the result for you to check.
3. Press **Write the result**. It saves the file and marks it resolved, and asks first if a conflict is still undecided.

The result is read-only while the agent is working.

### Handing all conflicts to the agent

![The Changes toolbar with conflicts waiting](media/sync-toolbar.webp)

**Resolve with Agent** in the Changes toolbar, or **Session › Ask the agent to resolve `n` conflicts**, asks the agent to resolve every conflicted file in the sandbox and stage it. Nothing is committed; you review the result in the diff, then press **Sync again**.

### Pull request sessions

In a session started from a pull request, sync follows the pull request's own branch. When the pull request conflicts with its base, use **Update from `<base>`** instead — see [Pull requests](/pull-requests).
